import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';

import { Form } from 'core/components';
import { useFormContext } from 'core/states';
import { TaxonomyGroupFormField } from 'taxonomy/components';
import { TaxonomyHelper } from 'taxonomy/helpers';
import { ITaxonomyGroup, ITaxonomyPath } from 'taxonomy/types';

interface IProps {
  defaultTaxonomyPath?: ITaxonomyGroup | ITaxonomyPath[];
  withoutForm?: boolean;
}

export const UpsertTaxonomyGroupFormFields = forwardRef(function UpsertTaxonomyGroupFormFields(
  { defaultTaxonomyPath = null, withoutForm = false }: IProps,
  ref
) {
  const { reset, watch, trigger, getValues } = useFormContext();

  const defaultValues = useMemo(
    () => ({
      taxonomy: Array.isArray(defaultTaxonomyPath)
        ? TaxonomyHelper.getLastLevelTaxonomy(defaultTaxonomyPath)
        : defaultTaxonomyPath,
    }),
    [defaultTaxonomyPath]
  );

  useEffect(() => {
    if (withoutForm) {
      return;
    }

    reset(defaultValues);
  }, [defaultValues, reset, withoutForm]);

  useImperativeHandle(
    ref,
    () => ({
      triggerFormSubmit: () => {
        return new Promise((resolve, reject) => {
          trigger()
            .then((isValid) => {
              if (isValid) {
                resolve(getValues());
              } else {
                reject();
              }
            })
            .catch(reject);
        });
      },
      getFormValues: () => getValues(),
    }),
    [getValues, trigger]
  );

  const selectedTaxonomy = watch('taxonomy');

  const fieldsStack = <TaxonomyGroupFormField key={selectedTaxonomy?.id} name="taxonomy" />;

  if (withoutForm) {
    return fieldsStack;
  }

  return (
    <Form isOpen inline withoutButtons withoutNotification>
      {fieldsStack}
    </Form>
  );
});
